import { useQuery } from '@tanstack/react-query';
import { endpoints } from '@/lib/api';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Activity, AlertCircle, Info, Server, Cpu } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNow } from '@/lib/hooks';

const REFETCH = 10_000;

const KIND_ICON = {
  service: Server,
  lxc: Cpu,
  sensor: Activity,
  alert: AlertCircle,
  pve: Activity,
  system: Info,
} as const;

const LEVEL_STYLE = {
  info: 'text-sky-400',
  warn: 'text-amber-400',
  error: 'text-rose-400',
} as const;

export function EventsWidget({ limit = 25 }: { limit?: number }) {
  const now = useNow(1_000);
  const q = useQuery({
    queryKey: ['events', limit],
    queryFn: () => endpoints.events.list(limit),
    refetchInterval: REFETCH,
  });
  const events = q.data?.events ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="h-3.5 w-3.5" />
          Recent events
        </CardTitle>
        <span className="text-xs text-muted-foreground">
          {events.length === 0 ? 'none yet' : `last ${events.length}`}
        </span>
      </CardHeader>
      <CardBody>
        {events.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            {q.isLoading ? 'loading…' : 'no events recorded yet'}
          </div>
        ) : (
          <ul className="space-y-1.5 max-h-96 overflow-y-auto">
            {events.map((e) => {
              const Icon = KIND_ICON[e.kind] ?? Info;
              const tone = e.level === 'error' ? 'rose' : e.level === 'warn' ? 'amber' : 'sky';
              return (
                <li
                  key={e.id}
                  className="flex items-start gap-2 text-xs"
                >
                  <Icon
                    className={cn(
                      'h-3.5 w-3.5 mt-0.5 shrink-0',
                      LEVEL_STYLE[e.level],
                    )}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono text-foreground/80">{e.subject}</span>
                      <span
                        className={cn(
                          'px-1.5 rounded text-[10px] uppercase tracking-wider border',
                          tone === 'rose' && 'bg-rose-500/10 border-rose-500/30 text-rose-300',
                          tone === 'amber' && 'bg-amber-500/10 border-amber-500/30 text-amber-300',
                          tone === 'sky' && 'bg-sky-500/10 border-sky-500/30 text-sky-300',
                        )}
                      >
                        {e.level}
                      </span>
                    </div>
                    <div className="text-foreground/70 truncate">{e.message}</div>
                  </div>
                  <span className="text-muted-foreground/70 whitespace-nowrap tabular-nums">
                    {relTime(e.timestamp, now)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function relTime(iso: string, now: number): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '—';
  const diff = Math.max(0, Math.floor((now - t) / 1000));
  if (diff === 0) return 'just now';
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}
